/* eslint-disable @typescript-eslint/consistent-type-definitions */
import '@mantine/core/styles.css';
import '@/global.css';
import '@mantine/notifications/styles.css';
import '@mantine/code-highlight/styles.css';
import '@mantine/dropzone/styles.css';

import { RouterProvider, createRouter } from '@tanstack/react-router';
import { MantineProvider } from '@mantine/core';
import { Auth0Provider, IdToken, useAuth0 } from '@auth0/auth0-react';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { cssVariablesResolver, theme } from './theme';
import { createApiClient as adminCreateApiClient } from '@/clients/admin.client';

import { routeTree } from './routeTree.gen';
import { FullScreenLoading } from './components/FullScreenLoading';
import { trackSignUpEvent } from './lib/analytics';
import {
  clearIssuer,
  isAuthorized,
  isFreeIssuerEnabled,
  isPersonalTenant,
  newUserInfo,
  prepareRejudgment,
  resolveIssuer,
  takePendingLoginHint,
  UserInfo,
} from './lib/auth';
import { logger } from './lib/logger';
import { useCallback, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RedirectToRoot } from './components/RedirectToRoot';
import axios, { AxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { Unauthorized, UnauthReason } from './components/Unauthorized';
import { LoginScreen } from './components/LoginScreen';

// ================================
// Setup Router

export interface MyRouterContext {
  auth: ReturnType<typeof useAuth0>;
  adminClient: ReturnType<typeof adminCreateApiClient>;
  userInfo: UserInfo;
  projectId?: string | null;
}

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
    adminClient: undefined!,
    userInfo: undefined!,
    projectId: null,
  },
  defaultPendingMs: 0,
  defaultNotFoundComponent: RedirectToRoot,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const gatewayURL = new URL('/api', import.meta.env.VITE_API_GATEWAY_URL).toString();

// ================================
// Setup Sentry

if (import.meta.env.VITE_SENTRY_DSN) {
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  const tracePropagationTargets = [gatewayURL];
  if (import.meta.env.DEV) {
    tracePropagationTargets.push('localhost');
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: parseInt(import.meta.env.VITE_SENTRY_SAMPLE_RATE),
    tracePropagationTargets,
    // Session Replay
    // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysSessionSampleRate: parseInt(import.meta.env.VITE_SENTRY_SESSION_SAMPLE_RATE),
    // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    replaysOnErrorSampleRate: 1.0,
  });
}

// ================================
// Setup Auth0

type InnerAppProps = {
  onLoginRequired: () => void;
};

function InnerApp({ onLoginRequired }: InnerAppProps) {
  const auth = useAuth0();
  const [context, setContext] = useState<MyRouterContext | null>(null);
  const [unauthReason, setUnauthReason] = useState<UnauthReason | null>(null);

  // Callbacks

  const getIdToken = useCallback(async (): Promise<IdToken> => {
    let claims = await auth.getIdTokenClaims();
    if (!claims?.exp || claims.exp < Date.now() / 1000) {
      // getAccessTokenSilently decides whether to use refresh token or not based on the accessToken expiration,
      // it does not refetch the idToken even if it is expired when cacheMode is 'on'.
      // So, we need to set cacheMode to 'off' to force refetching the idToken.
      try {
        await auth.getAccessTokenSilently({
          cacheMode: 'off',
        });
      } catch (error) {
        // If the refresh token endpoint returns a 403 error (e.g. the user was blocked after
        // upgrading to the paid issuer), log out the user and re-judge the issuer.
        logger.error('Failed to get Token:', error);
        prepareRejudgment(claims?.email);
        await auth.logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
        return {} as IdToken;
      }
      claims = await auth.getIdTokenClaims();
    }
    if (!claims) {
      await auth.loginWithRedirect({
        authorizationParams: {
          redirect_uri: window.location.origin,
        },
      });
      return {} as IdToken;
    }
    return claims;
  }, [auth]);

  const prepareContext = useCallback(async () => {
    const claims = await getIdToken();

    const userInfo = newUserInfo(claims);
    // Force logout if user does not have plamo role.
    if (!isAuthorized(userInfo)) {
      clearIssuer();
      return auth.logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }

    // Axios client
    const axiosInstance = axios.create({
      baseURL: gatewayURL,
      headers: {
        authorization: `Bearer ${claims.__raw}`,
      },
      validateStatus: (status: number) => {
        return status >= 200 && status < 300;
      },
    });
    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: {
        config: AxiosRequestConfig;
        response: { status: number; data?: { detail?: string } };
      }) => {
        // The personal tenant is switched off once its user moves to a paid org, while the
        // free-issuer token stays valid until it expires, so the issuer is re-judged now.
        if (
          error.response?.status === 403 &&
          error.response.data?.detail === 'Tenant is not active' &&
          isFreeIssuerEnabled()
        ) {
          prepareRejudgment(claims.email);
          await auth.logout({ logoutParams: { returnTo: window.location.origin } });
          throw error;
        }
        // When got 401, refresh the idToken and retry the request.
        if (error.response?.status === 401) {
          const claims = await getIdToken();
          // Update the idToken in the axios client.
          axiosInstance.defaults.headers.authorization = `Bearer ${claims.__raw}`;

          // Retry using the new idToken.
          const originalRequest = error.config;
          if (!originalRequest.headers) {
            throw error;
          }
          originalRequest.headers.authorization = `Bearer ${claims.__raw}`;
          return axiosInstance.request(originalRequest);
        }
        throw error;
      }
    );
    const adminClient = adminCreateApiClient(
      new URL('/api', import.meta.env.VITE_API_GATEWAY_URL).toString(),
      { axiosInstance }
    );

    // 有料化の完了画面に来た時点で個人テナントは inactive なので、呼ぶと 403 でログアウトしてしまう
    if (isPersonalTenant(userInfo) && window.location.pathname !== '/upgrade/completed') {
      try {
        const { created } = await adminClient.signup_v1_ensure_tenant(undefined);
        if (created) void trackSignUpEvent('sign_up', 'free', userInfo);
      } catch (error) {
        // 403 Tenant is not active は interceptor がログアウトさせるので、画面を組み立てない
        if (
          axios.isAxiosError<{ detail?: string }>(error) &&
          error.response?.status === 403 &&
          error.response.data?.detail === 'Tenant is not active' &&
          isFreeIssuerEnabled()
        ) {
          return;
        }
        // 計測のためだけの呼び出しなので、失敗しても画面の初期化は続ける
        logger.error(error);
      }
    }

    const context: MyRouterContext = {
      auth,
      userInfo,
      adminClient,
    };
    setContext(context);
  }, [auth, getIdToken]);

  // ここから下、認証の初期化処理

  useEffect(() => {
    if (auth.isLoading) {
      return;
    }

    const search = new URLSearchParams(window.location.search);
    const loginHint = takePendingLoginHint();
    if (!auth.isAuthenticated) {
      const err = search.get('error');
      if (err) {
        const errDesc = search.get('error_description');
        logger.error('error:', err, errDesc);
        if (errDesc?.startsWith('client requires organization membership')) {
          // どのorgにも属していない（利用登録していない & 招待もされていない）場合
          setUnauthReason('notBelongsAnyTenant');
        } else if (errDesc === 'email_is_already_in_use') {
          // 既に利用済みのメールアドレスで、かつ、別の認証方式でログインしようとした場合。
          // ただし、招待時に既存のメールアドレスが自動選択されるので、実質的には発生しない。
          setUnauthReason('emailAlreadyInUse');
        } else {
          setUnauthReason('unknown');
        }
        return;
      }

      // ログイン画面を経ていない（セッション切れなど）なら、ログイン画面で issuer を判定し直す。
      // ?issuer= で issuer を明示したときはそのままログインへ進む。
      const explicitIssuer = search.get('issuer');
      if (
        isFreeIssuerEnabled() &&
        !loginHint &&
        explicitIssuer !== 'paid' &&
        explicitIssuer !== 'free'
      ) {
        clearIssuer();
        onLoginRequired();
        return;
      }

      // 初回ログイン時の /welcome へのアクセスに限り、認証後 /welcome にリダイレクトする。
      // それ以外は、Origin にリダイレクトする。
      let redirectUri = window.location.origin;
      if (window.location.pathname === '/welcome') {
        redirectUri += '/welcome';
      }
      auth.loginWithRedirect({
        authorizationParams: {
          redirect_uri: redirectUri,
          login_hint: loginHint ?? undefined,
        },
        // chat_ui などから ?issuer= 付きで深いパスへ来たとき、ログイン後にそのパスへ戻す
        appState: { returnTo: window.location.pathname },
      });
      return;
    }

    prepareContext();
  }, [auth.isLoading, auth.isAuthenticated]);

  if (unauthReason) {
    return <Unauthorized reason={unauthReason} />;
  }

  if (!context) {
    return <FullScreenLoading />;
  }

  return <RouterProvider router={router} context={context} />;
}

export default function App() {
  const queryClient = new QueryClient();
  const [issuer, setIssuer] = useState(() => resolveIssuer(window.location.search));

  const app = (
    <MantineProvider
      theme={theme}
      cssVariablesResolver={cssVariablesResolver}
      defaultColorScheme="auto"
    >
      <QueryClientProvider client={queryClient}>
        <ModalsProvider
          modalProps={{ centered: true, size: 'lg' }}
          labels={{ confirm: 'OK', cancel: 'キャンセル' }}
        >
          {issuer ? (
            <InnerApp onLoginRequired={() => setIssuer(null)} />
          ) : (
            <LoginScreen onIssuerResolved={setIssuer} />
          )}
        </ModalsProvider>
        <Notifications position="top-right" top={60} bottom="auto" />
      </QueryClientProvider>
    </MantineProvider>
  );

  if (!issuer) {
    return app;
  }
  return (
    <Auth0Provider
      key={issuer}
      domain={
        issuer === 'free'
          ? import.meta.env.VITE_AUTH0_FREE_DOMAIN ?? ''
          : import.meta.env.VITE_AUTH0_DOMAIN
      }
      clientId={
        issuer === 'free'
          ? import.meta.env.VITE_AUTH0_FREE_CLIENT_ID ?? ''
          : import.meta.env.VITE_AUTH0_CLIENT_ID
      }
      authorizationParams={{
        redirect_uri: window.location.origin,
      }}
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      cacheLocation="localstorage"
      // router は起動時の URL を覚えているので、window.history ではなく router の history で戻す
      onRedirectCallback={(appState) =>
        router.history.replace(appState?.returnTo ?? window.location.pathname)
      }
    >
      {app}
    </Auth0Provider>
  );
}
